import Phaser from 'phaser';
import DrawLayer from './DrawLayer';
import GameMap from './GameMap';
import ABCyaPhaser from '@abcya/abcya_phaser';
import gameData from '../gameData';
import LifeLostPopup from '../ui/LifeLostPopup';
import PlayerPiece from './PlayerPiece';
import MapLocPointer from '../ui/MapLocPointer';
import ABCyaLib from '@abcya/abcya_lib';

export default class TripMazeGame extends Phaser.GameObjects.Container {
  /**
   * 
   * @param {Phaser.Scene} scene 
   */
  constructor(scene) {
    super(scene);

    this.gameConfig = gameData.gameConfigs[gameData.game];

    this.mapsData = null;
    this.destinationData = null;
    this.endPointer = null;
    this.playerXPos = null;
    this.playerYPos = null;
    this.downListener = null;
    this.levelStartTime = null;
    this.levelCompleteTime = null;
    this.newLevelBest = null;
    this.levelDestinationId = null;
    this.destinationSound = null;
    this.instSound = null;
    this.totalLives = null;
    this.livesRemaining = null;
    this.guideShowing = null;
    this.guideHand = null;
    this.quidePath = null;
    this.guideNeeded = null;
    this.pathComplete = null;
    this.crashIndicator = null;

    this.driveSound = null;
    this.DIRECTION_ROTATIONS = { up: 0,upright: 45,right: 90,downright: 135,down: 180,downleft: -135,left: -90,upleft: -45 };

    this.isDriving = false;
    this.playerIsHit = false;
    this.initTimer = true;

    /** @type {GameMap} */
    this.curMap = null;
    this.pathLayer = new DrawLayer(this.scene);
    this.add(this.pathLayer);

    this.pathLayer.on('PATH_COLLISION', this.handleHit, this);
    this.pathLayer.on('PATH_END_REACHED', this.handlePathComplete, this);

    this.playerPiece = new PlayerPiece(this.scene, gameData.playerId).setInteractive();
    this.add(this.playerPiece);

    this.playerPiece.on(Phaser.Input.Events.GAMEOBJECT_POINTER_DOWN, this.handleMoveStart, this);
    this.scene.input.on(Phaser.Input.Events.POINTER_UP, this.handleMoveStop, this);
    this.scene.input.on(Phaser.Input.Events.GAME_OUT, this.handleMoveStop, this);

    this.once(Phaser.GameObjects.Events.DESTROY, () => {
      this.scene.input.off(Phaser.Input.Events.POINTER_UP, this.handleMoveStop, this);
      this.scene.input.off(Phaser.Input.Events.GAME_OUT, this.handleMoveStop, this);
      this.scene.input.off(Phaser.Input.Events.POINTER_MOVE, this.handleMoving, this);

      this.instSound?.destroy();
      this.driveSound?.destroy();
      this.destinationSound?.destroy();
    });
  }

  handleHit (e) {
    if (this.playerIsHit) return;
    this.playerIsHit = true;
    this.isDriving = false;
    this.curMap.toggleShowObstacles(true);

    // add crash mark
    if (!this.crashIndicator) {
      this.crashIndicator = ABCyaPhaser.Images.addImage(0,0,'crash-mark', this);
    }
    this.crashIndicator.x = e.collisionInfo.x - this.x;
    this.crashIndicator.y = e.collisionInfo.y - this.y;
    this.crashIndicator.alpha = 0;
    this.crashIndicator.scale = 0;
    ABCyaPhaser.Tweens.addTween(this.crashIndicator, 250, { scale: 1, alpha: 1, completeDelay: 1000 }, Phaser.Math.Easing.Bounce.Out, () => this.crashIndicator.alpha = 0);

    if (this.gameConfig.DIFFICULTY === 'easy') {
      // only for junior version
      this.livesRemaining--;
      this.showLifeLost();
    }
    else {
      this.gameOver();
    }

    ABCyaPhaser.Audio.playOneShot(this.gameConfig.GAME_HIT_SOUND, this.scene, 1, false, 0, gameData.gameModeSoundAtlas);
  }

  handlePathComplete () {
    this.pathComplete = true;

    this.emit('TOGGLE_HUD', false);

    this.curMap.toggleShowObstacles(true);
    this.playerIsHit = false;
    this.isDriving = false;
    ABCyaPhaser.Tweens.addTween(this.endPointer, 500, { scale: 0, alpha: 0 }, Phaser.Math.Easing.Quadratic.Out);

    const endTime = this.scene.time.now;
    this.levelCompleteTime = Math.round(endTime - this.levelStartTime);

    // set new level completed data here so it takes effect if player leaves screen
    const levelData = gameData.allLevels[gameData.curLevelIndex];
    this.newLevelBest = this.levelCompleteTime < levelData.bestTime || levelData.bestTime === 0;
    if (this.newLevelBest) {
      levelData.bestTime = this.levelCompleteTime;
      ABCyaLib.Storage.saveObject(`${gameData.STORAGE_PREFIX + this.levelDestinationId}_best`, this.levelCompleteTime);
    }

    let medalStr = 'bronze';

    if ((this.gameConfig.DIFFICULTY === 'hard' && this.levelCompleteTime <= 5000) || (this.gameConfig.DIFFICULTY === 'easy' && this.levelCompleteTime <= 5000 && this.livesRemaining === 4)) {
      medalStr = 'gold';
    }
    else if ((this.gameConfig.DIFFICULTY === 'hard' && this.levelCompleteTime <= 10000) || (this.gameConfig.DIFFICULTY === 'easy' && this.levelCompleteTime <= 10000 && this.livesRemaining >= 3)) {
      medalStr = 'silver';
    }
    else {
      medalStr = 'bronze';
    }

    if (levelData.medal !== medalStr) {
      if (levelData.medal === '' || medalStr === 'gold' || (medalStr === 'silver' && levelData.medal === 'bronze')) {
        levelData.medal = medalStr;
        ABCyaLib.Storage.saveObject(`${gameData.STORAGE_PREFIX + this.levelDestinationId}_medal`, medalStr);
      }
    }

    ABCyaPhaser.Tweens.addTween(this, 1000, { y: 0 }, Phaser.Math.Easing.Quadratic.Out);

    const completeSound = ABCyaPhaser.Audio.createSound(this.scene, 1, false, 0, gameData.gameModeSoundAtlas);
    completeSound.once(Phaser.Sound.Events.COMPLETE, this.playDestinationSound, this);
    ABCyaPhaser.Audio.playSound(this.gameConfig.GAME_LEVEL_SOUND, completeSound);
  }

  showLifeLost () {
    this.emit('TOGGLE_HUD', false);

    const lifeName = this.gameConfig.PLAYER_DEMO_NAME === 'title-car' ? `player-piece-${gameData.playerId.toString()}` : 'idle';
    const lifePopUp = new LifeLostPopup(this.scene, this.livesRemaining,lifeName);
    lifePopUp.x = this.playerPiece.x;
    lifePopUp.y = this.playerPiece.y - 150;

    lifePopUp.scale = 0.75;
    this.add(lifePopUp);
    const nextCall = this.livesRemaining > 0 ? this.nextLife : this.gameOver;
    lifePopUp.once('LIVES_SHOWN', nextCall, this);
  }

  nextLife () {
    this.emit('TOGGLE_HUD', true);

    this.playerPiece.x = (this.pathLayer.lastEmptyOccupied.columnIndex * this.gameConfig.GRID_SIZE) + (this.gameConfig.GRID_SIZE / 2);
    this.playerPiece.y = (this.pathLayer.lastEmptyOccupied.rowIndex * this.gameConfig.GRID_SIZE) + (this.gameConfig.GRID_SIZE / 2);
    const pathDirection = this.curMap.getPathDirection(this.pathLayer.lastEmptyOccupied);
    this.playerPiece.angle = this.DIRECTION_ROTATIONS[pathDirection];

    this.playerIsHit = false;
    this.isDriving = false;
    this.pathLayer.restartPath();
  }

  gameOver () {
    this.emit('GAME_END');
  }

  playDestinationSound (sound) {
    sound.destroy();

    this.destinationSound = ABCyaPhaser.Audio.createSound(this.scene, 1, false, 0, ABCyaPhaser.Audio.LOCALIZATION_AUDIO_SPRITE);
    this.destinationSound.once(Phaser.Sound.Events.COMPLETE, this.finishLevelComplete, this);
    ABCyaPhaser.Audio.playSound(`${this.levelDestinationId}-sound`, this.destinationSound);

    this.emit('TOGGLE_RETURN_SKIP');
  }

  finishLevelComplete () {
    this.destinationSound?.destroy();
    this.destinationSound = null;

    this.emit('LEVEL_COMPLETE', { _newBest: this.newLevelBest, _timeComplete: this.levelCompleteTime });
  }

  handleMoveStart () {
    if (this.isDriving === false && this.playerIsHit === false && !this.pathComplete) {
      if (this.initTimer) {
        // reset the timer but only on level start
        this.initTimer = false;
        this.startTimer();
      }

      if (this.guideShowing) {
        // clear guide
        this.scene.tweens.killTweensOf(this.guideHand);
        this.guideHandTimer?.remove();
        this.guideHandTimer = null;
        this.guideHand.destroy();
        this.guideHand = null;
        this.guideShowing = false;
      }

      if (this.instSound !== null) {
        this.instSound.stop();
        this.instSound.destroy();
        this.instSound = null;
      }
      this.isDriving = true;
      this.curMap.toggleShowObstacles(false);
    }
  }

  handleMoveStop () {
    if (this.isDriving === true && this.playerIsHit === false) {
      this.isDriving = false;
    }
  }

  initGame (curLevelData) {
    this.levelDestinationId = curLevelData.destinationId.toLowerCase();

    // level data, map and destination
    if (this.curMap !== null) {
      this.curMap.destroy();
      this.curMap = null;
    }

    this.curMap = new GameMap(this.scene);
    this.addAt(this.curMap,0);
    this.curMap.once('MAP_SET_UP_COMPLETE', this.handleMapSetUp, this);
    this.curMap.setUpMap(curLevelData);
  }

  handleMapSetUp () {
    this.x = (this.scene.scale.width / 2) - (this.curMap.mapWidth / 2);
    this.y = this.scene.scale.height - this.curMap.mapHeight;

    if (!this.endPointer) this.endPointer = new MapLocPointer(this.scene);
    this.endPointer.x = (this.curMap.endBlock.columnIndex * this.gameConfig.GRID_SIZE) + (this.gameConfig.GRID_SIZE / 2);
    this.endPointer.y = (this.curMap.endBlock.rowIndex * this.gameConfig.GRID_SIZE) + (this.gameConfig.GRID_SIZE / 2);
    this.add(this.endPointer);

    this.startLevel(null, true);
  }

  startLevel (showGuide, resetTimer = false) {
    if (this.gameConfig.DIFFICULTY === 'easy') {
      this.totalLives = 4;
      this.livesRemaining = 4;
    }

    this.initTimer = resetTimer;

    // also stop destination sound if reset, may need to do that with menu too
    if (this.destinationSound !== null) {
      this.destinationSound.off(Phaser.Sound.Events.COMPLETE, this.finishLevelComplete, this, true);
      this.destinationSound.stop();
      this.destinationSound.destroy();
      this.destinationSound = null;
    }

    // set map to start position for reset;
    this.x = (this.scene.scale.width / 2) - (this.curMap.mapWidth / 2);
    this.y = this.scene.scale.height - this.curMap.mapHeight;
    this.endPointer.scale = 1;
    this.endPointer.alpha = 1;

    this.playerIsHit = false;
    this.isDriving = false;
    this.playerPiece.angle = 0;
    this.playerPiece.x = (this.curMap.startBlock.columnIndex * this.gameConfig.GRID_SIZE) + (this.gameConfig.GRID_SIZE / 2);
    this.playerPiece.y = (this.curMap.startBlock.rowIndex * this.gameConfig.GRID_SIZE) + (this.gameConfig.GRID_SIZE / 2);
    const pathDirection = this.curMap.getPathDirection(this.curMap.startBlock);
    this.playerPiece.angle = this.DIRECTION_ROTATIONS[pathDirection];

    this.pathComplete = false;
    this.pathLayer.initPath(this.playerPiece,this.curMap);

    this.scene.input.off(Phaser.Input.Events.POINTER_MOVE, this.handleMoving, this);
    this.scene.input.on(Phaser.Input.Events.POINTER_MOVE, this.handleMoving, this);

    this.newLevelBest = false;

    if (showGuide || gameData.guideNeeded === true) {
      this.startGuide();
      gameData.guideNeeded = false;
      if (this.gameConfig.GAME_TITLE === 'Food Groups') {
        this.instSound = ABCyaPhaser.Audio.createSound(this.scene, 1, false, 0, ABCyaPhaser.Audio.LOCALIZATION_AUDIO_SPRITE);
        ABCyaPhaser.Audio.playSound('FOOD PYRAMID-01', this.instSound);
      }
    }
  }

  startTimer() {
    this.levelStartTime = this.scene.time.now;
  }

  /**
   * 
   * @param {Phaser.Input.Pointer} pointer 
   */
  handleMoving (pointer) {
    if (!pointer.primaryDown) return;

    if (this.isDriving === true && this.playerIsHit === false) {
      this.playerXPos = pointer.x - this.x;
      this.playerYPos = pointer.y - this.y;
      this.playerPiece.x = this.playerXPos;
      this.playerPiece.y = this.playerYPos;
      this.pathLayer.addToPath({ x: this.playerXPos,y: this.playerYPos });
    }
  }

  resumeGame (nextAction, guide) {
    if (guide) this.guideNeeded = guide;

    // find next uncompleted level instead of just incrementing up
    let nextLevelNum = null;
    if (nextAction === 'next level') {
      let i = 0;
      while (i < gameData.allLevels.length && !nextLevelNum) {
        if (gameData.allLevels[i].bestTime === 0) {
          nextLevelNum = i;
          break;
        }
        i++;
      }
    }
    else {
      nextLevelNum = gameData.curLevelIndex;
    }

    if (nextLevelNum !== null) {
      gameData.curLevelIndex = nextLevelNum;
      this.initGame(gameData.allLevels[gameData.curLevelIndex]);
    }
    else {
      this.gameWon();
    }
  }

  gameWon () {
    this.emit('GAME_WON');
  }

  skipDestinationSound () {
    this.destinationSound.off(Phaser.Sound.Events.COMPLETE, this.finishLevelComplete, this, true);
    this.destinationSound.stop();
    this.destinationSound.destroy();
    this.destinationSound = null;

    this.finishLevelComplete();
  }

  startGuide () {
    // get path from map
    this.guidePath = this.curMap.shortestPath;
    this.guideHand = ABCyaPhaser.Images.addImage(0,0,'hand-pointer', this).setOrigin(0);

    const startBlock = this.guidePath[0];
    const blockPos = this.curMap.getBlockXY(startBlock);
    this.guideHand.x = blockPos.x;
    this.guideHand.y = blockPos.y;
    this.followGuidePath(startBlock);
    this.guideShowing = true;
  }

  followGuidePath (pathBlock) {
    if (this.guideHand === null || this.guideHand.scene === undefined) return;

    /** @type {Phaser.Time.TimerEvent} */
    this.guideHandTimer = null;
    const nextStepIndex = this.guidePath.indexOf(pathBlock) + 1;
    if (nextStepIndex > this.guidePath.length - 1) {
      // pause and then restart
      const restartBlock = this.guidePath[0];
      const restartPos = this.curMap.getBlockXY(restartBlock);

      this.guideHandTimer = this.scene.time.delayedCall(1000, () => {
        this.guideHand.setPosition(restartPos.x, restartPos.y);
        this.guideHandTimer = this.scene.time.delayedCall(500, this.followGuidePath,[restartBlock], this);
      });
    }
    else {
      const nextBlock = this.guidePath[nextStepIndex];
      const nextPathPos = this.curMap.getBlockXY(nextBlock);
      ABCyaPhaser.Tweens.addTween(this.guideHand, 100, { x: nextPathPos.x, y: nextPathPos.y }, Phaser.Math.Easing.Linear, () => this.followGuidePath(nextBlock));
    }
  }

  tick () {
    if (this.isDriving) {
      this.playerPiece.isMoving = true;
      if (!this.driveSound) {
        this.driveSound = ABCyaPhaser.Audio.createSound(this.scene, 1, false, 0, gameData.gameModeSoundAtlas);
        ABCyaPhaser.Audio.playSound(this.gameConfig.GAME_MOVE_SOUND, this.driveSound, 0.25, true);
      }
    }
    else {
      this.playerPiece.isMoving = false;
      if (this.driveSound) {
        this.driveSound.stop();
        this.driveSound.destroy();
        this.driveSound = null;
      }
    }

    if (this.gameConfig.PLAYER_DEMO_NAME === 'Ant') this.playerPiece.tick();
  }
}